import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import ReserveForZeroTag from 'components/Common/ReserveForZeroTagAndTooltip/ReserveForZeroTag'
import CruisesDiscountTsAndCsModal from 'components/Cruises/CruisesDiscountTsAndCsModal'
import TextButton from 'LuxKit/Button/TextButton'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowPriceCaptionLoadingSkeleton from 'LuxKit/PricePoints/PriceRowPriceCaptionLoadingSkeleton'
import PriceRowPriceLoadingSkeleton from 'LuxKit/PricePoints/PriceRowPriceLoadingSkeleton'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import PriceRowMarginText from 'components/OfferPage/PriceRowMarginText'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL } from 'constants/offer'
import ModalContext from 'contexts/ModalContext'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import { useAppSelector } from 'hooks/reduxHooks'
import { useDirectSearchPrices, useSearchPrices } from 'hooks/Search/useSearchPrices'
import { EmptyArray, unique } from 'lib/array/arrayUtils'
import { hasIncludedGuestsExceeded } from 'lib/checkout/cartReservationUtils'
import getLPCRegionLOS from 'lib/offer/getLPCRegionLOS'
import offerPageURL from 'lib/offer/offerPageURL'
import { isVillaOffer } from 'lib/offer/offerTypes'
import { isPackageEligibleForZeroReservation } from 'lib/offer/reserveForZeroUtils'
import {
  buildSuggestedDatesString,
  formatDateRangesForAlternativeDates,
  getShouldUseSearchPrices,
} from 'lib/search/searchUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import { numToWords } from 'lib/string/numToWords'
import { capitalise } from 'lib/string/stringUtils'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import moment from 'moment'
import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { getIsMultiRoomEnabled } from 'selectors/featuresSelectors'
import { getIsSpecificCampaignPage } from 'selectors/routerSelectors'
import { CHARTER_PAGE_SLUG } from 'constants/charterFlight'

interface Props {
  offer: App.Offer | App.OfferSummary
  className?: string
  pkg: App.Package
  availableRate?: App.OfferAvailableRate
  hasDates?: boolean
  align?: 'start' | 'center' | 'end'
  desktopAlign?: 'start' | 'center' | 'end'
  /**
   * @default The offer page URL with no parameters
   */
  offerUrl?: string
  checkInDate?: moment.Moment
  filters?: App.OfferListFilters
  showCompact?: boolean
  isPricePerNight?: boolean
  hideIncludesTaxesAndFees?: boolean
  duration?: number
  hidePrice?: boolean
  overrideRoomsNumber?: number
  /**
   * Total number of guests (adults + children + infants) for Maldives Charter bundle
   */
  totalGuests?: number
}

interface MappedStateProps {
  isSpoofed: boolean
  pathname: string
  isMaldivesCharter: boolean
}
// Export is only temporary for experiment
export const URGENCY_TAG_THRESHOLD = 5

function OfferTilePricing(props: Props & MappedStateProps) {
  const {
    offer,
    className,
    pkg,
    availableRate,
    hasDates,
    align,
    desktopAlign,
    offerUrl,
    checkInDate,
    isSpoofed,
    pathname,
    filters,
    showCompact = false,
    isPricePerNight,
    hideIncludesTaxesAndFees = false,
    duration,
    hidePrice = false,
    overrideRoomsNumber,
    isMaldivesCharter,
    totalGuests,
  } = props

  const offerMetadata = useOfferMetaData(offer.id, filters)
  const isCruise = (offer.holidayTypes ?? []).map(i => i.toLowerCase()).includes('cruises')
  const isVillas = isVillaOffer(offer)
  const rooms = filters?.rooms ?? EmptyArray
  const showMarginInSpoofMode = useAppSelector(state => state.system.showMarginInSpoofMode)

  const directSearchPrices = useDirectSearchPrices({ filters: filters ?? {}, offerId: offer.id })
  const {
    defaultPricing,
    memberPricing,
    bundlePricing,
    bundleMemberPricing,
    bundlePricingPerPerson,
    bundleMemberPricingPerPerson,
    hotelPrice,
    hotelMemberPrice,
    showMemberPrice,
    propertyFees,
    suggestedDates,
  } = useSearchPrices({
    offer,
    pkg,
    rate: availableRate,
    duration,
    filters,
    useRealSearchPrices: getShouldUseSearchPrices(filters),
    showPerNightPricing: isPricePerNight && offer.type === OFFER_TYPE_HOTEL,
  })

  const useLuxPlusPricing = useAppSelector(getCanViewLuxPlusBenefits)
  const isMultiRoomEnable = useAppSelector(state => getIsMultiRoomEnabled(state, offer))
  const discountPercent = (showMemberPrice && memberPricing && useLuxPlusPricing) ? memberPricing.discountPercent : defaultPricing.discountPercent

  const hasSuggestedCheckInCheckOut = suggestedDates?.checkIn && suggestedDates?.checkOut
  const valueToDisplay = defaultPricing.value
  const taxesAndFees = (availableRate?.taxesAndFees ?? pkg.taxesAndFees) || 0
  const showPriceDetails = taxesAndFees > 0 || propertyFees > 0 || config.agentHub.isEnabled

  const shouldShowValue = pkg.value > 0 && !!discountPercent && pkg.shouldDisplayValue &&
    (offer.type == OFFER_TYPE_HOTEL || !pkg.roomRate?.inclusionsHideValue)

  const showIncludedGuestsExceeded = useMemo(() => {
    if (directSearchPrices) {
      return false
    }

    if (!pkg.roomType || (pkg.roomRate?.extraGuestSurcharges?.length || 0) > 0) {
      return false
    }

    return rooms?.some(room => hasIncludedGuestsExceeded(room, pkg, offer))
  }, [directSearchPrices, pkg, rooms, offer])

  const shouldShowRate = !offer.isDiscountPillHidden && discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  let staysFrom = offer.minDuration
  const LPCRegionLOS = getLPCRegionLOS(offer)
  if (LPCRegionLOS && offer.packages.some(p => p.duration === LPCRegionLOS)) {
    staysFrom = LPCRegionLOS
  }
  const showStaysFromLabel = !showCompact && !hasDates && offer.type === OFFER_TYPE_ALWAYS_ON && LPCRegionLOS && (staysFrom ?? 0) < (duration ?? 0)

  const isReserveForZeroEnabled = config.RESERVE_FOR_ZERO_ENABLED
  const isReservableForZero = (isReserveForZeroEnabled || isSpoofed) && isPackageEligibleForZeroReservation(offer, pkg.roomRate?.isReservableForZero, checkInDate)
  const getDuration = useCallback((singular: string, count: number) => {
    if (isCruise && count) {
      // FOR FLASH CRUISES WE NEED TO
      // SUBTRACT 1 NIGHT FROM THE DURATION.
      // BECAUSE DURATION COMES IN DAYS RATHER THAN NIGHTS.
      // E.G. 7 DAYS CRUISE IS 6 NIGHTS
      return pluralizeToString(singular, count - 1)
    }
    return pluralizeToString(singular, count)
  }, [isCruise])

  const guestsDisplayText = useMemo(() => {
    if (!isMaldivesCharter || !totalGuests) return
    const guestsWord = numToWords(totalGuests).trim()
    const capitalizedGuests = capitalise(guestsWord)
    return `${capitalizedGuests} ${totalGuests === 1 ? 'person' : 'people'}`
  }, [isMaldivesCharter, totalGuests])

  const utmSource = useAppSelector(state => state.utm.source)
  const noGoogleAffiliations = utmSource !== 'google'
  const durationText = useMemo(() => {
    const isFlexibleSearch = filters?.flexibleNights && suggestedDates?.checkIn && suggestedDates?.checkOut
    const isAnytimeSearch = (!filters?.checkIn && !filters?.checkOut) && !isFlexibleSearch
    const roomCount = overrideRoomsNumber || filters?.rooms?.length || 0
    const isMultipleRoom = roomCount > 0
    const roomText = isMultiRoomEnable && isMultipleRoom ? `, ${pluralizeToString(offer.saleUnit, roomCount)}` : ''
    let text: string
    if (directSearchPrices && offerMetadata?.extendedAvailability) {
      text = `${formatDateRangesForAlternativeDates(offerMetadata.extendedAvailability.checkIn, offerMetadata.extendedAvailability.checkOut)} (${getDuration('night', pkg.duration)})${roomText} from`
    } else if (directSearchPrices?.duration) {
      text = `${getDuration('night', directSearchPrices.duration)}${roomText} from`
    } else if (hasDates) {
      text = `${getDuration('night', pkg.duration)}${roomText} from`
    } else if (!isAnytimeSearch && hasSuggestedCheckInCheckOut && suggestedDates) {
      text = `${buildSuggestedDatesString(suggestedDates.checkIn, suggestedDates.checkOut)}${roomText} from`
    } else if (suggestedDates && duration) {
      text = `${getDuration('night', duration)}${roomText} from`
    } else if (pkg.duration) {
      text = `${getDuration('night', pkg.duration)}${roomText} from`
    } else if (offer.tileDurationLabel) {
      text = `${offer.tileDurationLabel.toLocaleLowerCase()}${roomText} from`
    } else {
      text = 'From'
    }
    if (offer.type === OFFER_TYPE_ALWAYS_ON && noGoogleAffiliations && !showCompact) {
      text += ' (room only)'
    }
    // Add "+ flights" in bundle mode before "from"
    if (isMaldivesCharter && text.endsWith(' from')) {
      text = text.replace(' from', ' + flights from')
    }
    return text
  }, [filters?.flexibleNights,
    filters?.checkIn,
    filters?.checkOut,
    filters?.rooms?.length,
    suggestedDates,
    overrideRoomsNumber,
    isMultiRoomEnable,
    offer.saleUnit,
    offer.tileDurationLabel,
    offer.type,
    directSearchPrices,
    offerMetadata?.extendedAvailability,
    hasDates,
    hasSuggestedCheckInCheckOut,
    duration,
    pkg.duration,
    noGoogleAffiliations,
    isMaldivesCharter,
    getDuration,
    showCompact])

  const durationResolved = useMemo(() => {
    if (hasDates) return pkg.duration
    if (suggestedDates && duration) return duration
    if (pkg.duration) return pkg.duration
  }, [duration, hasDates, pkg.duration, suggestedDates])

  const saleUnit = useMemo(() => {
    if (isVillas) return 'total'
    // In bundle mode, decide based on price mode
    if (isMaldivesCharter) {
      return filters?.isPricePerPerson ? 'person' : 'total'
    }
    if (rooms.length) return 'total'
    if (isPricePerNight) return 'night'
    if (offer.saleUnit.toLowerCase() === 'cruise') return 'person'
    return offer.saleUnit
  }, [isPricePerNight, isVillas, offer.saleUnit, rooms.length, filters?.isPricePerPerson, isMaldivesCharter])

  const showModal = useContext(ModalContext)
  const openCruiseDiscountTermsAndConditionsModal = useCallback(() => {
    showModal(<CruisesDiscountTsAndCsModal />)
  }, [showModal])

  const defaultPricingToDisplay = useMemo(() => {
    // Use bundle pricing if in bundle mode and bundle pricing is available
    // Choose per-person or total based on isPricePerPerson flag
    let pricing = defaultPricing

    if (isMaldivesCharter) {
      if (filters?.isPricePerPerson && bundlePricingPerPerson) {
        pricing = bundlePricingPerPerson
      } else if (bundlePricing) {
        pricing = bundlePricing
      }
    }

    return {
      ...pricing,
      saleUnit,
    }
  }, [isMaldivesCharter, bundlePricing, bundlePricingPerPerson, defaultPricing, saleUnit, filters?.isPricePerPerson])

  const memberPricingToDisplay = useMemo(() => {
    // Use bundle member pricing if in bundle mode and bundle member pricing is available
    // Choose per-person or total based on isPricePerPerson flag
    let pricing = memberPricing
    if (isMaldivesCharter) {
      if (filters?.isPricePerPerson && bundleMemberPricingPerPerson) {
        pricing = bundleMemberPricingPerPerson
      } else if (bundleMemberPricing) {
        pricing = bundleMemberPricing
      }
    }
    if (!pricing) return undefined

    return {
      ...pricing,
      saleUnit,
    }
  }, [isMaldivesCharter, bundleMemberPricing, bundleMemberPricingPerPerson, memberPricing, saleUnit, filters?.isPricePerPerson])

  const offerLocations = useMemo(() => unique(offer.locations.concat(offer.location)),
    [offer.locations, offer.location])
  const vendorName = offer.vendorName

  const isSearchPage = pathname.includes('search')
  const dispatchOfferListEvent = useContext(OfferListEventsContext)

  const marginLPC = pkg.roomRate?.marginPercentage
  const marginValueLPC = marginLPC ? hotelPrice * marginLPC / 100 : 0
  const displayMarginLPC = Boolean(isSpoofed && showMarginInSpoofMode && marginLPC && marginValueLPC)

  useEffect(() => {
    const durationToDispatch = getShouldUseSearchPrices(filters) ? directSearchPrices?.duration : durationResolved
    const availabilityToDispatch = getShouldUseSearchPrices(filters) ? offerMetadata?.available : defaultPricingToDisplay.price !== 0
    const roomRateId = getShouldUseSearchPrices(filters) ? offerMetadata?.pricing?.lowestPriceRoomRateId : undefined

    if (!getShouldUseSearchPrices(filters) || offerMetadata) {
      dispatchOfferListEvent({
        type: OfferListEvents.pricingCalculated,
        price: hidePrice ? undefined : defaultPricingToDisplay.price,
        roomRateId: hidePrice ? undefined : roomRateId,
        memberPrice: hidePrice ? undefined : memberPricingToDisplay?.price,
        duration: hidePrice ? undefined : durationToDispatch,
        extendedAvailability: offerMetadata?.extendedAvailability,
        availability: !!availabilityToDispatch,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerMetadata])

  // Show skeleton when in bundle mode but bundle pricing not yet available (flight data still loading)
  const showPriceSkeleton = isMaldivesCharter && !bundlePricing

  if (hidePrice) {
    return null
  }

  return (
    <Group
      className={className}
      direction="vertical"
      horizontalAlign={align}
      desktopHorizontalAlign={desktopAlign}
      gap={4}
    >
      <Group direction="vertical" horizontalAlign={align} desktopHorizontalAlign={desktopAlign} noDisplayWhenEmpty>
        {isReservableForZero && <ReserveForZeroTag />}
      </Group>
      <Group direction="vertical" horizontalAlign={align} desktopHorizontalAlign={desktopAlign}>
        {showPriceSkeleton && (
          <>
            <PriceRowPriceCaptionLoadingSkeleton width="15ch" />
            <PriceRowPriceLoadingSkeleton size={showCompact ? 'S' : 'L'} />
          </>
        )}
        {!showPriceSkeleton && (
          <>
            <PriceRowPriceCaption>{durationText}</PriceRowPriceCaption>
            <LuxPlusPriceStack
              pricing={defaultPricingToDisplay}
              memberPricing={memberPricingToDisplay}
              size={showCompact ? 'S' : 'L'}
              testid="search-price"
              desktopHorizontalAlign={desktopAlign}
              offerConfig={offer.luxPlus}
            />
          </>
        )}
        {shouldShowValue && isCruise && !isPricePerNight && <PriceRowValueDiscountWithCaption
          data-testid="value-box"
          size="M"
          originalValue={Math.ceil(valueToDisplay)}
          discountPercentage={shouldShowRate ? discountPercent : undefined}
          onInfoIconClick={openCruiseDiscountTermsAndConditionsModal}
        />}
        {showStaysFromLabel && <PriceRowCaption>
          Stays from {staysFrom} available
        </PriceRowCaption>}
        {!!pkg.roomOccupancy && offer.type === 'tour' && <PriceRowCaption>{pkg.roomOccupancy}</PriceRowCaption>}
        {showIncludedGuestsExceeded && (
          <AdditionalGuestsPopup
            complex={false}
            description="+ Extra guest surcharge"
            modalContent={pkg.roomPolicyDescription}
          />
        )}
        {shouldShowValue && !isCruise && <PriceRowValueDiscountWithCaption
          data-testid="value-box"
          size="M"
          originalValue={Math.ceil(valueToDisplay)}
          discountPercentage={shouldShowRate ? discountPercent : undefined}
        />}
        {guestsDisplayText && <PriceRowCaption>{guestsDisplayText}</PriceRowCaption>}
        <Group direction="vertical" horizontalAlign={align} desktopHorizontalAlign={desktopAlign} gap={4}>
          {showPriceDetails && durationResolved && <OfferPriceDetailsRow
            trigger="price-row"
            triggerSize="M"
            offer={offer}
            duration={durationResolved}
            propertyFees={propertyFees}
            hotelPrice={hotelPrice}
            taxesAndFees={taxesAndFees}
            rooms={rooms}
            extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
            dueAtPropertyMessage={offer.property?.taxesAndFeesContent ? offer.property?.taxesAndFeesContent : ''}
            hotelMemberPrice={hotelMemberPrice}
            showMemberPrice={showMemberPrice}
            context={isSearchPage ? 'search' : undefined}
            hideIncludesTaxesAndFees={hideIncludesTaxesAndFees}
            cta={
              <TextButton kind="primary" fit="flex" to={offerUrl ?? offerPageURL(offer)}>
                View Offer
              </TextButton>
            }
          />}
          {displayMarginLPC && (<PriceRowMarginText marginValue={marginValueLPC} margin={marginLPC! / 100} size="M" type="le_estimate" colour="critical" />)}
          <PriceRowAgentHubCommission
            size="L"
            productType={offer.productType}
            offerId={offer.id}
            offerLocations={offerLocations}
            vendorName={vendorName}
            packageId={pkg.id}
          />
        </Group>
      </Group>
    </Group>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>((appState) => {
  return {
    isSpoofed: appState.auth.account.isSpoofed,
    pathname: appState.router.location.pathname,
    isMaldivesCharter: getIsSpecificCampaignPage(appState, CHARTER_PAGE_SLUG)
  }
})(OfferTilePricing)
